<template>
  <section class="certification-change-notes-accordion">
    <button
      type="button"
      class="certification-change-notes-accordion__toggle"
      :aria-expanded="String(expanded)"
      @click="toggle"
    >
      Change notes
      <feather-icon :icon="expanded ? 'ChevronUpIcon' : 'ChevronDownIcon'" svgClasses="h-4 w-4" />
    </button>
    <certification-change-notes-list
      v-if="expanded"
      :notes="notes"
      :loading="loading"
      :error="error"
      :loadMoreError="loadMoreError"
      :hasMore="hasMore"
      :loadingMore="loadingMore"
      @load-more="loadMore"
    />
  </section>
</template>

<script>
import { mapActions } from 'vuex'
import CertificationChangeNotesList from './CertificationChangeNotesList.vue'

export default {
  name: 'CertificationChangeNotesAccordion',
  components: { CertificationChangeNotesList },
  props: {
    certificationId: { type: String, required: true },
    certificationVersionId: { type: String, required: true },
    certificationExamId: { type: String, default: null },
  },
  data: () => ({
    expanded: false,
    notes: [],
    loading: false,
    error: '',
    loadMoreError: '',
    loadedScopeKey: null,
    page: 1,
    hasMore: false,
    loadingMore: false,
    requestId: 0,
  }),
  computed: {
    scopeKey() {
      return `${this.certificationVersionId}:${this.certificationExamId || ''}`
    },
    // Notes shown for this tab: the Certification itself, this version and
    // this version's exam.
    entityIds() {
      return [
        this.certificationId,
        this.certificationVersionId,
        this.certificationExamId,
      ].filter(Boolean)
    },
  },
  watch: {
    scopeKey() {
      this.requestId += 1
      this.expanded = false
      this.notes = []
      this.error = ''
      this.loadMoreError = ''
      this.loadedScopeKey = null
      this.page = 1
      this.hasMore = false
      this.loading = false
      this.loadingMore = false
    },
  },
  methods: {
    ...mapActions('certification', ['getCertificationChangeNotesAction']),
    async toggle() {
      this.expanded = !this.expanded
      if (this.expanded) await this.fetchNotes()
    },
    // Called after a save so an open list shows the new note. A closed list
    // just drops its cache and refetches when opened.
    async refresh() {
      this.requestId += 1
      this.loadedScopeKey = null
      this.error = ''
      this.loadMoreError = ''
      if (this.expanded) await this.fetchNotes(true)
    },
    async fetchNotes(force = false) {
      if ((!force && this.loading) || this.loadedScopeKey === this.scopeKey)
        return

      const requestId = ++this.requestId
      const scopeKey = this.scopeKey
      const entityIds = this.entityIds
      this.loading = true
      this.error = ''
      this.loadMoreError = ''
      try {
        const response = await this.getCertificationChangeNotesAction({
          certificationId: this.certificationId,
          entityIds,
          page: 1,
        })
        if (requestId !== this.requestId || scopeKey !== this.scopeKey) return
        this.notes = (response && response.results) || []
        this.page = 1
        this.hasMore = !!(response && response.next)
        this.loadedScopeKey = this.scopeKey
      } catch (error) {
        if (requestId === this.requestId && scopeKey === this.scopeKey)
          this.error = 'Unable to load change notes.'
      } finally {
        if (requestId === this.requestId && scopeKey === this.scopeKey)
          this.loading = false
      }
    },
    async loadMore() {
      if (this.loading || this.loadingMore || !this.hasMore) return

      const requestId = this.requestId
      const scopeKey = this.scopeKey
      const entityIds = this.entityIds
      this.loadingMore = true
      this.loadMoreError = ''
      try {
        const response = await this.getCertificationChangeNotesAction({
          certificationId: this.certificationId,
          entityIds,
          page: this.page + 1,
        })
        if (requestId !== this.requestId || scopeKey !== this.scopeKey) return
        this.notes = [...this.notes, ...((response && response.results) || [])]
        this.page += 1
        this.hasMore = !!(response && response.next)
      } catch (error) {
        if (requestId === this.requestId && scopeKey === this.scopeKey)
          this.loadMoreError = 'Unable to load more change notes.'
      } finally {
        if (requestId === this.requestId && scopeKey === this.scopeKey)
          this.loadingMore = false
      }
    },
  },
}
</script>

<style scoped>
.certification-change-notes-accordion {
  border-top: 1px solid #dedede;
  margin-top: 24px;
  padding-top: 16px;
}
.certification-change-notes-accordion__toggle {
  align-items: center;
  background: transparent;
  border: 0;
  color: rgba(var(--vs-primary), 1);
  cursor: pointer;
  display: flex;
  font-size: 1rem;
  font-weight: 600;
  gap: 6px;
  padding: 0;
}
</style>
